import { IconButton } from "@package/subframe/components/IconButton";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import { Tabs } from "@package/subframe/components/Tabs";
import {
  FeatherArrowLeft,
  FeatherChevronLeft,
  FeatherChevronRight,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { endOfDay, format, startOfDay } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";

import { ChecklistOverviewTab } from "@/components/dashboard/ChecklistOverviewTab";
import {
  ComplianceTasksTab,
  countOverdueDefinitions,
} from "@/components/dashboard/ComplianceTasksTab";
import { EventRecordsTab } from "@/components/dashboard/EventRecordsTab";
import { FollowUpActionsTab } from "@/components/dashboard/FollowUpActionsTab";
import { LogHistoryTab } from "@/components/dashboard/LogHistoryTab";
import { OpenIncidentsCard } from "@/components/dashboard/OpenIncidentsCard";
import { DashboardOverview } from "@/components/dashboard/overview/DashboardOverview";
import { Calendar } from "@/components/ui/calendar";
import { DesktopOnlyNotice } from "@/components/ui/desktop-only-notice";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useListClosuresQueryControllerList } from "@/generated/closures/closures";
import { useListTaskDefinitionsByNurseryQueryControllerList } from "@/generated/compliance-tasks/compliance-tasks";
import { useListActionsByNurseryQueryControllerListByNursery } from "@/generated/follow-up-actions/follow-up-actions";
import {
  ComplianceTaskDefinitionResponseDto,
  FollowUpActionResponseDto,
  FollowUpActionResponseDtoStatus,
  ScheduledQuestionnaireListItemDto,
} from "@/generated/model";
import { useListScheduledQuestionnairesControllerListScheduledQuestionnaires } from "@/generated/scheduled-questionnaires/scheduled-questionnaires";
import { useFeatureGate } from "@/hooks/use-feature-gate";
import { useIsMobile } from "@/hooks/use-mobile";
import { useOfflineOpenIncidents } from "@/hooks/use-offline-open-incidents";
import { isDashboardPageDataKey } from "@/lib/dashboard/dashboard-refresh";
import {
  type DashboardPeriod,
  DEFAULT_PERIOD,
  isDashboardPeriod,
  periodLabels,
} from "@/lib/dashboard/period";
import { getPeriodRange } from "@/lib/date-range";
import { getUkToday, isUkToday, parseDateStr } from "@/lib/date-utils";
import { FEATURES } from "@/lib/features";

type DashboardTab =
  | "follow-up"
  | "checklists"
  | "logs"
  | "compliance"
  | "events";

export default function DashboardPage() {
  const { user, userRoleType, isLoading: authLoading } = useAuth();
  const { selectedNurseryId } = useNursery();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/dashboard" });
  const queryClient = useQueryClient();

  // Compliance is BOOST+ only — when the org doesn't have the feature, hide
  // the tab and rewrite an inbound `?tab=compliance` URL to the default tab so
  // managers don't land on a tab that isn't there.
  const { hasFeature: hasComplianceFeature, isResolving: complianceResolving } =
    useFeatureGate(FEATURES.COMPLIANCE_TASKS);

  const activeTab: DashboardTab =
    search.tab === "checklists"
      ? "checklists"
      : search.tab === "logs"
        ? "logs"
        : search.tab === "events"
          ? "events"
          : search.tab === "compliance" && hasComplianceFeature
            ? "compliance"
            : "follow-up";

  const setActiveTab = (tab: DashboardTab) => {
    navigate({
      to: "/dashboard",
      search: { ...search, tab },
      replace: true,
    });
  };

  const period: DashboardPeriod = isDashboardPeriod(search.period)
    ? search.period
    : DEFAULT_PERIOD;

  const selectedDate = useMemo(() => {
    if (search.date) {
      const parsed = parseDateStr(search.date);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return getUkToday();
  }, [search.date]);

  const setPeriod = (value: DashboardPeriod) => {
    navigate({
      to: "/dashboard",
      search: { ...search, period: value },
      replace: true,
    });
  };

  const setSelectedDate = (date: Date) => {
    navigate({
      to: "/dashboard",
      search: { ...search, date: format(date, "yyyy-MM-dd") },
      replace: true,
    });
  };

  // Redirect nursery staff (non-managers) to checklists
  const isNurseryStaff =
    userRoleType === "NURSERY_DEPUTY_MANAGER" ||
    userRoleType === "NURSERY_LEADER" ||
    userRoleType === "NURSERY_PRACTITIONER";

  useEffect(() => {
    if (!authLoading && isNurseryStaff) {
      navigate({ to: "/checklists", replace: true });
    }
  }, [isNurseryStaff, authLoading, navigate]);

  // The dashboard route stays mounted across the in-app overview⇄Today toggle
  // (`?view=`), so the page-level queries' `refetchOnMount: "always"` doesn't
  // re-fire the way it does on a real revisit. This effect is the imperative
  // counterpart to that declarative refresh: on a view switch it force-refetches
  // those page-level data queries so a toggle shows current data too (NAU-303).
  // The first render is skipped — initial load is covered by refetchOnMount.
  const prevViewRef = useRef(search.view);
  useEffect(() => {
    if (prevViewRef.current === search.view) return;
    if (!selectedNurseryId) return;
    // Record the view only once we actually refetch, so a toggle during the
    // brief window before the nursery resolves isn't swallowed.
    prevViewRef.current = search.view;
    void queryClient.refetchQueries({
      type: "active",
      predicate: (q) => isDashboardPageDataKey(q.queryKey, selectedNurseryId),
    });
  }, [search.view, selectedNurseryId, queryClient]);

  const dateRange = useMemo(() => getPeriodRange(period), [period]);

  // Fetch scheduled questionnaires for stats cards
  const { data: statsResponse } =
    useListScheduledQuestionnairesControllerListScheduledQuestionnaires(
      selectedNurseryId || "",
      {
        includeAnswers: true,
        includeCompletedBy: true,
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      },
      { query: { enabled: !!selectedNurseryId, refetchOnMount: "always" } },
    );

  // Calculate date range for selected day
  const selectedDayRange = useMemo(() => {
    return {
      startDate: startOfDay(selectedDate),
      endDate: endOfDay(selectedDate),
    };
  }, [selectedDate]);

  // Fetch scheduled questionnaires for the selected day
  const { data: dayResponse, isLoading: dayLoading } =
    useListScheduledQuestionnairesControllerListScheduledQuestionnaires(
      selectedNurseryId || "",
      {
        includeAnswers: true,
        includeCompletedBy: true,
        startDate: selectedDayRange.startDate.toISOString(),
        endDate: selectedDayRange.endDate.toISOString(),
      },
      { query: { enabled: !!selectedNurseryId, refetchOnMount: "always" } },
    );

  const dayItems = useMemo(
    () =>
      (dayResponse?.data?.items as ScheduledQuestionnaireListItemDto[]) || [],
    [dayResponse],
  );

  // Closure covering the selected day — drives the "Closed" indicator (NAU-231).
  const { data: closuresResponse } = useListClosuresQueryControllerList(
    selectedNurseryId || "",
    { query: { enabled: !!selectedNurseryId } },
  );
  const selectedDayClosure = useMemo(() => {
    const day = format(selectedDate, "yyyy-MM-dd");
    return (
      (closuresResponse?.data ?? []).find(
        (c) => c.startDate <= day && day <= c.endDate,
      ) ?? null
    );
  }, [closuresResponse, selectedDate]);

  // Fetch follow-up actions for the nursery (dashboard view: all active + completed last 7 days)
  const { data: actionsResponse, isLoading: actionsLoading } =
    useListActionsByNurseryQueryControllerListByNursery(
      selectedNurseryId || "",
      { dashboardView: true },
      { query: { enabled: !!selectedNurseryId, refetchOnMount: "always" } },
    );

  // Also fetch day-scoped actions for the checklists tab's action badges
  const { data: dayActionsResponse } =
    useListActionsByNurseryQueryControllerListByNursery(
      selectedNurseryId || "",
      {
        startDate: selectedDayRange.startDate.toISOString(),
        endDate: selectedDayRange.endDate.toISOString(),
      },
      {
        query: {
          enabled: !!selectedNurseryId && activeTab === "checklists",
          refetchOnMount: "always",
        },
      },
    );

  // Build a map of scheduledQuestionnaireId -> action status breakdown (for checklists tab)
  const sqActionsInfo = useMemo(() => {
    const map = new Map<
      string,
      { total: number; open: number; overdue: number; completed: number }
    >();
    const actions =
      (dayActionsResponse?.data as FollowUpActionResponseDto[]) || [];
    for (const action of actions) {
      // Only group questionnaire-sourced actions by SQ id; compliance-sourced
      // actions are surfaced through their own views.
      if (!action.scheduledQuestionnaireId) continue;
      const existing = map.get(action.scheduledQuestionnaireId) || {
        total: 0,
        open: 0,
        overdue: 0,
        completed: 0,
      };
      existing.total++;
      if (action.status === FollowUpActionResponseDtoStatus.OVERDUE)
        existing.overdue++;
      else if (action.status === FollowUpActionResponseDtoStatus.COMPLETED)
        existing.completed++;
      else if (
        action.status === FollowUpActionResponseDtoStatus.OPEN ||
        action.status === FollowUpActionResponseDtoStatus.WAITING_APPROVAL
      )
        existing.open++;
      map.set(action.scheduledQuestionnaireId, existing);
    }
    return map;
  }, [dayActionsResponse?.data]);

  // All dashboard actions (grouped by status in the tab component)
  const dashboardActions = useMemo(() => {
    return (actionsResponse?.data as FollowUpActionResponseDto[]) || [];
  }, [actionsResponse?.data]);

  // Fetch compliance task definitions for the manager dashboard. Unfiltered so
  // we can compute the overdue count for the tab label even when the tab is
  // not active; the tab itself does category/active filtering client-side.
  // Only enabled when the org has the BOOST+ feature — otherwise the request
  // would 403 on the FeatureGate guard.
  const { data: complianceDefsResponse, isLoading: complianceLoading } =
    useListTaskDefinitionsByNurseryQueryControllerList(
      selectedNurseryId || "",
      { limit: 200 },
      {
        query: {
          enabled:
            !!selectedNurseryId && !complianceResolving && hasComplianceFeature,
          refetchOnMount: "always",
        },
      },
    );

  const complianceDefinitions = useMemo<
    ComplianceTaskDefinitionResponseDto[]
  >(() => {
    const body = complianceDefsResponse?.data as
      | {
          data?: ComplianceTaskDefinitionResponseDto[];
          items?: ComplianceTaskDefinitionResponseDto[];
        }
      | ComplianceTaskDefinitionResponseDto[]
      | undefined;
    if (!body) return [];
    if (Array.isArray(body)) return body;
    return body.data ?? body.items ?? [];
  }, [complianceDefsResponse]);

  const complianceOverdueCount = useMemo(
    () => countOverdueDefinitions(complianceDefinitions),
    [complianceDefinitions],
  );

  // Map scheduledQuestionnaireId -> checklist name for display
  const sqNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const items =
      (statsResponse?.data?.items as ScheduledQuestionnaireListItemDto[]) || [];
    for (const item of items) {
      map.set(item.id, item.template.name);
    }
    for (const item of dayItems) {
      map.set(item.id, item.template.name);
    }
    return map;
  }, [statsResponse?.data?.items, dayItems]);

  const statsItems = useMemo(
    () =>
      (statsResponse?.data?.items as ScheduledQuestionnaireListItemDto[]) || [],
    [statsResponse?.data?.items],
  );

  const [calendarOpen, setCalendarOpen] = useState(false);
  const isToday = isUkToday(selectedDate);

  const goToPreviousDay = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    setSelectedDate(prevDay);
  };

  const goToNextDay = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    setSelectedDate(nextDay);
  };

  // Open fridge incidents (NAU-219) — surfaced in the overview's priority list
  // and Today's-Status rollup.
  const { items: openIncidents } = useOfflineOpenIncidents(
    selectedNurseryId || "",
  );

  // Show loading while checking auth or redirecting
  if (authLoading || isNurseryStaff) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  const isTodayView = search.view === "today";

  // Period selector, shared by the overview header and the Today header.
  const periodSelector = (
    <Select
      label=""
      placeholder="This week"
      helpText=""
      size="large"
      value={period}
      onValueChange={(value: string) => setPeriod(value as DashboardPeriod)}
    >
      {Object.entries(periodLabels).map(([key, label]) => (
        <Select.Item key={key} value={key} size="large">
          {label}
        </Select.Item>
      ))}
    </Select>
  );

  // Default landing experience: the redesigned read-only overview (NAU-247).
  if (!isTodayView) {
    return (
      <DashboardOverview
        inputs={{
          statsItems,
          dayItems,
          actions: dashboardActions,
          complianceDefinitions,
          complianceOverdueCount,
          openIncidents,
        }}
        nurseryId={selectedNurseryId || ""}
        firstName={user?.firstName}
        period={period}
        periodSelector={periodSelector}
      />
    );
  }

  // "Today" — the per-day tabbed operational view. Not yet optimised for small
  // viewports, so managers on mobile get a desktop notice here (the overview
  // above is responsive).
  if (isMobile) {
    return <DesktopOnlyNotice />;
  }

  return (
    <div className="flex h-full w-full gap-8 flex-col items-start bg-white overflow-y-auto">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1 p-0">
        <LinkButton
          icon={<FeatherArrowLeft />}
          onClick={() => navigate({ to: "/dashboard", search: {} })}
        >
          Back to dashboard
        </LinkButton>
        <div className="flex w-full flex-wrap items-center gap-4">
          <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
            Today
          </span>
          <div className="flex items-center justify-end gap-2">
            {periodSelector}
          </div>
        </div>
      </div>

      {/* Stats Cards + Tab Content */}
      <div className="flex w-full flex-col items-start gap-6 px-0 pb-8">
        {selectedNurseryId && (
          <OpenIncidentsCard nurseryId={selectedNurseryId} />
        )}

        {/* Day navigation + Tabs */}
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-center justify-between pt-2">
            <Tabs>
              <Tabs.Item
                active={activeTab === "follow-up"}
                onClick={() => setActiveTab("follow-up")}
              >
                Follow-up actions
              </Tabs.Item>
              <Tabs.Item
                active={activeTab === "checklists"}
                onClick={() => setActiveTab("checklists")}
              >
                Checklists
              </Tabs.Item>
              <Tabs.Item
                active={activeTab === "logs"}
                onClick={() => setActiveTab("logs")}
              >
                Monitoring
              </Tabs.Item>
              <Tabs.Item
                active={activeTab === "events"}
                onClick={() => setActiveTab("events")}
              >
                Events
              </Tabs.Item>
              {hasComplianceFeature && (
                <Tabs.Item
                  active={activeTab === "compliance"}
                  onClick={() => setActiveTab("compliance")}
                >
                  Compliance
                  {complianceOverdueCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-error-500 px-2 text-caption-bold font-caption-bold text-white">
                      {complianceOverdueCount} overdue
                    </span>
                  )}
                </Tabs.Item>
              )}
            </Tabs>
            {activeTab === "checklists" && (
              <div className="flex items-center justify-center gap-2 flex-none">
                <IconButton
                  icon={<FeatherChevronLeft />}
                  onClick={goToPreviousDay}
                />
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-28 text-center text-body font-body text-subtext-color whitespace-nowrap hover:text-neutral-900 cursor-pointer"
                    >
                      {isToday ? "Today" : format(selectedDate, "d MMM yyyy")}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (date) {
                          setSelectedDate(date);
                          setCalendarOpen(false);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <IconButton
                  icon={<FeatherChevronRight />}
                  onClick={goToNextDay}
                />
              </div>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === "follow-up" ? (
            <FollowUpActionsTab
              actions={dashboardActions}
              isLoading={actionsLoading}
              sqNameMap={sqNameMap}
            />
          ) : activeTab === "logs" ? (
            <LogHistoryTab
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
            />
          ) : activeTab === "events" ? (
            <EventRecordsTab
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
            />
          ) : activeTab === "compliance" ? (
            <ComplianceTasksTab
              definitions={complianceDefinitions}
              isLoading={complianceLoading}
            />
          ) : (
            <ChecklistOverviewTab
              dayItems={dayItems}
              dayLoading={dayLoading}
              selectedDate={selectedDate}
              sqActionsInfo={sqActionsInfo}
              closure={selectedDayClosure}
            />
          )}
        </div>
      </div>
    </div>
  );
}
